import React, { FC, useState } from 'react'
import { Form } from 'semantic-ui-react'
import { RouteComponentProps } from 'react-router-dom'

import {
  Layout,
  Header,
  Footer,
  TextInput,
  CheckboxInput,
  ContentSeparator,
  ErrorBanner,
  FloatingButton
} from '@components'
import { validateFormField, useAuthToken, navigateToUrl, routes } from '@utils'
import { registerCaregiverApi, setSmsOptingApi } from '@apiRoutes'
import styles from './styles.module.scss'
import _model from './model.en.json'
import configs from './configs'

type Localization = {
  title: string
  description: string
  serverError: string
  form: {
    [field: string]: {
      label: string
      placeholder: string
      error: string
    }
  }
}

type TextState = {
  value: string
  hasError: boolean
  error: string
}

type RequestData = {
  firstName: string
  lastName: string
  zipcode: string
  phone: string
}

const model = _model as Localization

const SetupCaregiverProfile: FC<RouteComponentProps> = ({ history }) => {
  /** ************* */
  /* General data handlers */

  const [isLoading, setLoading] = useState(false)
  const [screenError, setScreenError] = useState('' as string)

  /** ************* */
  /* Form data handlers */

  const clearError = (
    state: TextState,
    updateState: (state: TextState) => void
  ) => {
    const { hasError } = state

    if (!hasError) return

    updateState({ ...state, hasError: false, error: '' })
  }

  /** ************* */
  const [firstName, setFirstName] = useState({
    value: '',
    hasError: false,
    error: ''
  } as TextState)

  const updateFirstName = (val: string): void => {
    setFirstName({ ...firstName, value: val })
  }

  const clearFirstNameError = (): void => {
    clearError(firstName, setFirstName)
  }

  /** ************* */
  const [lastName, setLastName] = useState({
    value: '',
    hasError: false,
    error: ''
  } as TextState)

  const updateLastName = (val: string): void => {
    setLastName({ ...lastName, value: val })
  }

  const clearLastNameError = (): void => {
    clearError(lastName, setLastName)
  }

  /** ************* */
  const [zipcode, setZipcode] = useState({
    value: '',
    hasError: false,
    error: ''
  } as TextState)

  const updateZipcode = (val: string): void => {
    setZipcode({ ...zipcode, value: val })
  }

  const clearZipcodeError = (): void => {
    clearError(zipcode, setZipcode)
  }

  /** ************* */
  const [phone, setPhone] = useState({
    value: '+1',
    hasError: false,
    error: ''
  } as TextState)

  const updatePhone = (val: string): void => {
    setPhone({ ...phone, value: val })
  }

  const clearPhoneError = (): void => {
    clearError(phone, setPhone)
  }
  /** ************* */
  const [smsOptIn, setSmsOptIn] = useState({
    value: false
  } as { value: boolean })

  const updateSmsOptIn = (val: boolean): void => {
    setSmsOptIn({ value: val })
  }

  /** ************* */
  /* Fetch existing data */

  const { authToken } = useAuthToken()

  /** ************* */
  /* Form authentication handlers */

  let validForm = true

  const verifyFormField = (
    name: string,
    state: TextState,
    updateState: (state: TextState) => void
  ): void => {
    const hasValidationError = validateFormField(name, configs, state)
    if (!hasValidationError) return

    validForm = false
    const {
      form: {
        [name]: { error }
      }
    } = model

    const newState = { ...state, hasError: true }
    if (error) {
      newState.error = error
    }
    updateState(newState)
  }

  const validateForm = () => {
    validForm = true
    const fields = [
      {
        name: 'firstName',
        state: firstName,
        updateState: setFirstName
      },
      {
        name: 'lastName',
        state: lastName,
        updateState: setLastName
      },
      {
        name: 'zipcode',
        state: zipcode,
        updateState: setZipcode
      },
      {
        name: 'phone',
        state: phone,
        updateState: setPhone
      }
    ]

    fields.forEach(({ name, state, updateState }) => {
      verifyFormField(name, state, updateState)
    })
  }

  const submitSmsOptIn = async () => {
    if (!smsOptIn.value) {
      navigateToUrl(history, routes.setupPatientProfile)
      return
    }

    if (!authToken) {
      setLoading(false)
      return
    }

    const response = await setSmsOptingApi(authToken, smsOptIn.value)

    if (!response.success) {
      setScreenError(model.serverError)
      setLoading(false)
      return
    }

    navigateToUrl(history, routes.setupPatientProfile)
  }

  const submitProfile = async () => {
    setScreenError('')

    if (isLoading || !authToken) {
      return
    }

    setLoading(true)

    const data = {
      firstName: firstName.value,
      lastName: lastName.value,
      zipcode: zipcode.value,
      phone: phone.value
    } as RequestData

    const response = await registerCaregiverApi(authToken, data)

    if (!response.success) {
      setScreenError(model.serverError)
      setLoading(false)
      return
    }

    submitSmsOptIn()
  }

  const handleSubmit = () => {
    validateForm()

    if (!validForm) {
      return
    }

    submitProfile()
  }

  /** ************* */
  /* UI Content */

  const renderErrorBanner = () => {
    if (!screenError) {
      return null
    }

    return (
      <div className={styles.errorContainer}>
        <ErrorBanner message={screenError} />
      </div>
    )
  }

  const renderForm = () => {
    const {
      form: {
        firstName: { label: firstNameLabel, placeholder: firstNamePlaceholder },
        lastName: { label: lastNameLabel, placeholder: lastNamePlaceholder },
        zipcode: { label: zipcodeLabel, placeholder: zipcodePlaceholder },
        phone: { label: phoneLabel, placeholder: phonePlaceholder },
        smsOptIn: { label: smsOptInLabel }
      }
    } = model

    return (
      <div className={styles.form}>
        <Form>
          <Form.Group widths="equal">
            <TextInput
              type="alphanumeric"
              label={firstNameLabel}
              placeholder={firstNamePlaceholder}
              value={firstName.value}
              handleChange={updateFirstName}
              error={firstName.hasError}
              errorMessage={firstName.error}
              onFocus={clearFirstNameError}
            />
            <TextInput
              type="alphanumeric"
              label={lastNameLabel}
              placeholder={lastNamePlaceholder}
              value={lastName.value}
              handleChange={updateLastName}
              error={lastName.hasError}
              errorMessage={lastName.error}
              onFocus={clearLastNameError}
            />
          </Form.Group>
          <Form.Group widths="equal">
            <TextInput
              type="zipcode"
              label={zipcodeLabel}
              placeholder={zipcodePlaceholder}
              value={zipcode.value}
              handleChange={updateZipcode}
              error={zipcode.hasError}
              errorMessage={zipcode.error}
              onFocus={clearZipcodeError}
            />
          </Form.Group>
          <ContentSeparator extraClassName={styles.separator} />
          <Form.Group widths="equal">
            <TextInput
              type="telephone"
              label={phoneLabel}
              placeholder={phonePlaceholder}
              value={phone.value}
              handleChange={updatePhone}
              error={phone.hasError}
              errorMessage={phone.error}
              onFocus={clearPhoneError}
            />
          </Form.Group>
          <Form.Group widths="equal">
            <CheckboxInput
              id="smsOptIn"
              label={smsOptInLabel}
              checked={smsOptIn.value}
              handleChange={updateSmsOptIn}
            />
          </Form.Group>
        </Form>
      </div>
    )
  }

  const renderSubmitContent = () => {
    return (
      <div className={styles.buttonContainer}>
        <FloatingButton
          id="submit"
          onClick={handleSubmit}
          disabled={!authToken}
          isLoading={isLoading}
        />
      </div>
    )
  }

  return (
    <Layout>
      <div className={styles.container}>
        <Header title={model.title} description={model.description} />
        {renderErrorBanner()}
        {renderForm()}
        {renderSubmitContent()}
        <Footer />
      </div>
    </Layout>
  )
}

export default SetupCaregiverProfile
